import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Users,
  DollarSign,
  TrendingDown,
  Briefcase,
  Eye,
  CalendarDays,
  SlidersHorizontal as Filters,
  X,
  ChevronDown,
  ChevronUp,
  LayoutDashboard,
  RefreshCw
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../api/axios';
import styles from './Payroll.module.css';
import Pagination from '../../../components/Common/Pagination/Pagination';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import { useLoading } from '../../../context/LoadingContext';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import BulkGeneratePayrollDrawer from './BulkGeneratePayrollDrawer';

const Payroll = () => {
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoading();
  const mainContentRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [isBulkDrawerOpen, setIsBulkDrawerOpen] = useState(false);
  const [monthlyData, setMonthlyData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [yearFilter, setYearFilter] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 15,
  });

  const fetchMonthlySummary = useCallback(async (page = 1, isPartial = false, customPerPage = null) => {
    if (isPartial) {
      setIsRefreshing(true);
    } else {
      showLoader();
    }
    const perPageToUse = customPerPage || pagination.per_page;
    try {
      const response = await api.get('/payroll/monthly-summary', {
        params: { page, per_page: perPageToUse },
        skipLoader: isPartial
      });
      if (response.data.success) {
        setMonthlyData(response.data.data.data);
        setPagination({
          current_page: response.data.data.current_page,
          last_page: response.data.data.last_page,
          total: response.data.data.total,
          per_page: response.data.data.per_page,
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch payroll data');
    } finally {
      if (isPartial) {
        setIsRefreshing(false);
      } else {
        hideLoader();
      }
    }
  }, [pagination.per_page, showLoader, hideLoader]);

  useEffect(() => {
    fetchMonthlySummary(1);
  }, [fetchMonthlySummary]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      fetchMonthlySummary(page);
    }
  };

  const formatCurrency = (amount) => {
    const val = parseFloat(amount) || 0;
    const display = val < 0 ? 0 : val;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(display);
  };

  const getStatusSummary = (row) => {
    const parts = [];
    if (row.generated_count > 0) parts.push(`${row.generated_count} Generated`);
    if (row.approved_count > 0) parts.push(`${row.approved_count} Approved`);
    if (row.paid_count > 0) parts.push(`${row.paid_count} Paid`);
    if (row.draft_count > 0) parts.push(`${row.draft_count} Draft`);
    if (row.cancelled_count > 0) parts.push(`${row.cancelled_count} Cancelled`);
    return parts;
  };

  const getBatchStatusBadge = (status, progress) => {
    if (!status) return <span style={{ color: '#94a3b8' }}>-</span>;

    const lower = String(status).toLowerCase();
    let badgeClass = styles['statusDraft'];
    if (lower === 'completed' || lower === 'finished') {
      badgeClass = styles['statusPaid'];
    } else if (lower === 'pending') {
      badgeClass = styles['statusApproved'];
    } else if (lower === 'processing' || lower === 'in_progress') {
      badgeClass = styles['statusGenerated'];
    } else if (lower === 'failed' || lower === 'error') {
      badgeClass = styles['statusCancelled'];
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}>
        <span className={`${styles.statusBadge} ${badgeClass}`}>
          {status}
        </span>
        {progress && (
          <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', paddingLeft: '2px' }}>
            {progress}
          </span>
        )}
      </div>
    );
  };

  // Filter client-side if searching or filtering year
  const filteredData = monthlyData.filter(row => {
    const monthYearStr = `${row.month_name} ${row.year}`.toLowerCase();
    const matchesSearch = !searchTerm || monthYearStr.includes(searchTerm.toLowerCase());
    const matchesYear = !yearFilter || String(row.year) === String(yearFilter);
    return matchesSearch && matchesYear;
  });

  // Compute grand totals from fetched data
  const totalEmployees = monthlyData.reduce((sum, r) => sum + parseInt(r.total_employees || 0), 0);
  const totalNet = monthlyData.reduce((sum, r) => sum + parseFloat(r.total_net || 0), 0);
  const totalDeductions = monthlyData.reduce((sum, r) => sum + parseFloat(r.total_deductions || 0), 0);
  const totalGross = monthlyData.reduce((sum, r) => sum + parseFloat(r.total_gross || 0), 0);

  const availableYears = Array.from(new Set(monthlyData.map(r => r.year))).map(y => ({
    value: String(y),
    label: String(y)
  }));

  return (
    <div className={styles['payroll-container']}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']} ref={mainContentRef}>
          <Breadcrumbs customItems={[
            { label: 'HR', path: '#' },
            { label: 'Payroll / Salary', path: '/employees/payroll' }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Payroll Management</h1>
              <p>Monthly payroll overview — click a month to see employee details.</p>
            </div>
            <div className={styles['header-actions']}>
              <button onClick={() => setIsBulkDrawerOpen(true)} className={styles['btn-secondary']}>
                <CalendarDays size={18} />
                <span>Bulk Generate</span>
              </button>
              <button onClick={() => navigate('/employees/payroll/create')} className={styles['add-btn']}>
                <Plus size={20} />
                <span>Add Payroll</span>
              </button>
            </div>
          </div>

          {/* Summary Collapsible */}
          {monthlyData.length > 0 && (
            <div className={styles['summary-collapsible']}>
              <button
                className={styles['summary-toggle']}
                onClick={() => setIsSummaryOpen(prev => !prev)}
              >
                <div className={styles['summary-toggle-left']}>
                  <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                  <div className={styles['summary-toggle-text']}>
                    <span className={styles['summary-toggle-title']}>Summary</span>
                    <span className={styles['summary-toggle-hint']}>
                      {totalEmployees} Employees · {formatCurrency(totalNet)} Net Payout
                    </span>
                  </div>
                </div>
                {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>

              <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
                <div className={`${styles['metric-card']} ${styles['purple']}`}>
                  <div className={styles['metric-icon-box']}>
                    <Users size={24} />
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>Total Payrolls</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{totalEmployees}</span>
                    </div>
                  </div>
                </div>

                <div className={`${styles['metric-card']} ${styles['green']}`}>
                  <div className={styles['metric-icon-box']}>
                    <DollarSign size={24} />
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>Net Salary</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{formatCurrency(totalNet)}</span>
                    </div>
                  </div>
                </div>

                <div className={`${styles['metric-card']} ${styles['orange']} ${styles['highlight-card']}`}>
                  <div className={styles['metric-icon-box']}>
                    <TrendingDown size={24} />
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>Total Deductions</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{formatCurrency(totalDeductions)}</span>
                    </div>
                  </div>
                  <div className={styles['glow-effect']}></div>
                </div>

                <div className={`${styles['metric-card']} ${styles['blue']}`}>
                  <div className={styles['metric-icon-box']}>
                    <Briefcase size={24} />
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>Gross Salary</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{formatCurrency(totalGross)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Table Controls */}
          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search Month or Year..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: pagination.per_page, label: `${pagination.per_page} per page` }}
                  onChange={(option) => {
                    if (option && option.value) {
                      setPagination(prev => ({ ...prev, per_page: option.value, current_page: 1 }));
                      fetchMonthlySummary(1, false, option.value);
                    }
                  }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 15, label: '15 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' }
                  ]}
                />
              </div>

              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {yearFilter && <span className={styles['filter-badge']}></span>}
              </button>

              <button
                className={styles['filter-btn']}
                onClick={() => fetchMonthlySummary(pagination.current_page, true)}
                disabled={isRefreshing}
                title="Refresh Table Data"
              >
                <RefreshCw size={18} className={isRefreshing ? 'spin' : ''} />
                <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Payroll Summary</h3>
                  <p>Filter records by year.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Year</label>
                  <CustomSelect
                    value={yearFilter ? { value: yearFilter, label: yearFilter } : null}
                    onChange={(opt) => setYearFilter(opt ? opt.value : '')}
                    options={availableYears}
                    placeholder="All Years"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['btn-reset']} onClick={() => setYearFilter('')}>Reset</button>
                <button className={styles['btn-apply']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          {/* Desktop Monthly Table */}
          <div className={styles['custom-table-container']} style={{ opacity: isRefreshing ? 0.6 : 1, transition: 'opacity 0.2s ease-in-out' }}>
            <div className={styles['custom-table-wrapper']}>
              <table className={styles['custom-table']}>
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Total Employees</th>
                    <th>Gross Salary</th>
                    <th>Total Deductions</th>
                    <th>Net Salary</th>
                    <th>Batch Status</th>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 ? (
                    <tr>
                      <td colSpan="7" className={styles.noData}>
                        No payroll records found. Generate payroll to get started.
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((row) => (
                      <tr
                        key={`${row.year}-${row.month}`}
                        className={styles['monthly-row']}
                        onClick={() => navigate(`/employees/payroll/month/${row.year}/${row.month}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <div className={styles['month-cell']}>
                            <div className={styles['month-icon']}>
                              <CalendarDays size={20} />
                            </div>
                            <div className={styles['month-info']}>
                              <span className={styles['month-name']}>{row.month_name} {row.year}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={styles['employee-count-badge']}>
                            <Users size={14} /> {row.total_employees}
                          </span>
                        </td>
                        <td>
                          <span className={styles['text-bold']}>{formatCurrency(row.total_gross)}</span>
                        </td>
                        <td>
                          <span className={styles['text-danger']}>{formatCurrency(row.total_deductions)}</span>
                        </td>
                        <td>
                          <span className={`${styles['text-bold']} ${styles['text-success']}`}>{formatCurrency(row.total_net)}</span>
                        </td>
                        <td>
                          {getBatchStatusBadge(row.batch_status, row.batch_progress)}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            className={styles['control-btn']}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/employees/payroll/month/${row.year}/${row.month}`);
                            }}
                            style={{ padding: '6px 14px', height: 'auto', borderRadius: '8px', marginLeft: 'auto' }}
                          >
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards View */}
            <div className={styles['payroll-cards']}>
              {filteredData.length === 0 ? (
                <div className={styles.noData}>No payroll records found.</div>
              ) : (
                filteredData.map((row) => (
                  <div
                    key={`${row.year}-${row.month}`}
                    className={styles['payroll-card']}
                    onClick={() => navigate(`/employees/payroll/month/${row.year}/${row.month}`)}
                  >
                    <div className={styles['card-main']}>
                      <div className={styles['card-info']}>
                        <span className={styles['card-id']}>{row.month_name} {row.year}</span>
                        <span className={styles['card-amount']}>{formatCurrency(row.total_net)}</span>
                      </div>
                      <button
                        className={styles['control-btn']}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/employees/payroll/month/${row.year}/${row.month}`);
                        }}
                        style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '8px' }}
                      >
                        <Eye size={14} /> View
                      </button>
                    </div>

                    <div className={styles['card-details']}>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Batch Status</span>
                        <span className={styles['detail-value']}>
                          {row.batch_status || 'N/A'} {row.batch_progress ? `(${row.batch_progress})` : ''}
                        </span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Total Employees</span>
                        <span className={styles['detail-value']}>{row.total_employees}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Gross Salary</span>
                        <span className={styles['detail-value']}>{formatCurrency(row.total_gross)}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Deductions</span>
                        <span className={`${styles['detail-value']} ${styles['has-balance']}`}>{formatCurrency(row.total_deductions)}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Net Salary</span>
                        <span className={styles['detail-value']}>{formatCurrency(row.total_net)}</span>
                      </div>
                    </div>

                    <div className={styles['card-footer']}>
                      <div className={styles['footer-statuses']}>
                        {getStatusSummary(row).map((part, i) => (
                          <span key={i} className={styles['status-pill']}>{part}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {pagination.total > 0 && (
              <div className={styles['desktop-pagination']}>
                <div className={styles['pagination-container']}>
                  <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.last_page}
                    onPageChange={handlePageChange}
                    totalItems={pagination.total}
                    itemsPerPage={pagination.per_page}
                  />
                </div>
              </div>
            )}
          </div>

          {pagination.total > 0 && (
            <div className={styles['mobile-pagination']}>
              <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.last_page}
                onPageChange={handlePageChange}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
              />
            </div>
          )}

          <BulkGeneratePayrollDrawer
            isOpen={isBulkDrawerOpen}
            onClose={() => setIsBulkDrawerOpen(false)}
            onSuccess={() => fetchMonthlySummary(1)}
          />

        </main>

        <Footer />
      </div>
    </div>
  );
};

export default Payroll;

